import { CustomAnalyticsQueryVersionSchema } from "@redotech/redo-analytics-custom-analytics/query/custom-analytics-report-query";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const CustomReportAstStageSchema = z.enum([
  "parse",
  "plan",
  "sql",
  "preview",
  "done",
]);

const CustomReportAstColumnSchema = z.object({
  key: z.string(),
  sourceName: z.string(),
  label: z.string(),
  sqlType: z.string(),
  kind: z.string(),
});

const SnowflakeSqlBindSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.union([z.string(), z.number()])),
]);

const CustomReportAstSnowflakeSchema = z.object({
  boundSql: z.string(),
  inlineSql: z.string(),
  bindings: z.array(SnowflakeSqlBindSchema),
  reportTimeZone: z.string(),
  placeholderScope: z.boolean(),
});

export const validateCustomReportAst = unaryRpc({
  name: "validateCustomReportAst" as const,
  description:
    "Parse, validate, and compile a hand-written custom analytics query AST for the given query version. Optionally executes a row-capped preview against Snowflake when a team is supplied. Writes nothing.",
  input: z.object({
    version: CustomAnalyticsQueryVersionSchema,
    ast: z.unknown(),
    teamId: z.string().trim().optional(),
    preview: z.boolean().default(false),
    previewLimit: z.number().int().min(1).max(100).default(50),
  }),
  output: z.object({
    ok: z.boolean(),
    stage: CustomReportAstStageSchema,
    error: z.string().nullable(),
    normalizedAst: z.unknown().nullable(),
    queryPlan: z.unknown().nullable(),
    queryPlanVersion: z.number().nullable(),
    snowflake: CustomReportAstSnowflakeSchema.nullable(),
    columns: z.array(CustomReportAstColumnSchema).nullable(),
    rows: z.array(z.record(z.string(), z.unknown())).nullable(),
  }),
});

export type ValidateCustomReportAstInput = z.infer<
  typeof validateCustomReportAst.input
>;
export type ValidateCustomReportAstOutput = z.infer<
  typeof validateCustomReportAst.output
>;
